import {
  LitElement,
  css,
  html,
  nothing,
} from 'https://cdn.jsdelivr.net/gh/lit/dist@2/all/lit-all.min.js'
import { debounce } from 'lib-utils'
import styles from 'lib-styles'
import * as paths from 'lib-paths'

class NovaQuantityInput extends LitElement {
  static properties = {
    key: {},
    label: {},
    initial: { type: Number },
    value: { type: Number, state: true },
    type: { type: String },
    max: { type: String, attribute: 'variant-max' },
    maxQuantity: { type: Number },
    variantId: { type: String, attribute: 'data-variant-id' },
    template: { type: String, attribute: 'data-template' },
    messageText: { type: String, attribute: 'data-reach-limit-text' },
    warningMessage: { type: String, state: true },
    isWarningVisible: { type: Boolean, state: true },
  }

  constructor() {
    super()
    this.id = Math.random() * 100000
    this.debounceBroadcast = debounce(this.broadcast, 500)
    this.max = ''
    this.maxQuantity = 0
    this.variantId = ''
    this.template = ''
    this.messageText = ''
    this.warningMessage = ''
    this.isWarningVisible = false
    this.value = 0
  }

  connectedCallback() {
    super.connectedCallback()

    this.maxQuantity = Number(this.max)

    if (this.template === 'product')
      document.addEventListener('nova:cartData:updated', this.updateMaxQuantity)
  }

  updated(changedProperties) {
    if (changedProperties.has('variantId') || changedProperties.has('max')) {
      this.maxQuantity = Number(this.max)
      if (this.template === 'product') this.updateMaxQuantity()
    }
  }

  updateMaxQuantity = () => {
    this.maxQuantity = Number(this.max)
    if (window.cartData) {
      for (const item of window.cartData) {
        if (Number(item.variant_id) === Number(this.variantId)) {
          this.maxQuantity -= Number(item.quantity)
        }
      }
    }
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('initial')) {
      this.value = this.initial
      // this.debounceBroadcast()
    }
  }

  broadcast = () => {
    this.dispatchEvent(
      new CustomEvent('changeQuantityStarted', {
        bubbles: true,
        composed: true,
        detail: {
          key: this.key,
          value: this.value,
        },
      })
    )
  }

  changeValue(newValue) {
    if (this.max === '') {
      this.value = newValue
      this.hideWarning()
      this.debounceBroadcast()
    } else {
      if (newValue <= this.maxQuantity) {
        this.value = newValue
        this.hideWarning()
        this.broadcast()
      } else {
        this.showWarning()
      }
    }
  }

  increment() {
    this.changeValue(this.value + 1)
  }

  decrement() {
    if (this.value > 0) {
      this.changeValue(this.value - 1)
    }
  }

  override(e) {
    const newValue = Math.max(0, e.target.value)
    this.changeValue(newValue)
  }

  showWarning() {
    this.warningMessage = this.messageText
    this.isWarningVisible = true
    clearTimeout(this._warningTimeout)
    this._warningTimeout = setTimeout(() => {
      this.isWarningVisible = false
    }, 2000)
  }

  hideWarning() {
    this.isWarningVisible = false
  }

  render() {
    return html`
      <span>
        <div class="wrapper">
          <button
            @click=${this.decrement}
            name="decrease"
            class="button__decrease"
          >
            ${paths.minus}
          </button>
          <label class="sr-only" for="Quantity-${this.id}">
            ${this.label || 'Quantity'}
          </label>
          <slot name="input"></slot>
          <input
            id="Quantity-${this.id}"
            class="input"
            type="number"
            name="updates[]"
            .value=${this.value}
            @change="${this.override}"
            min="0"
            ${this.max !== '' ? html`max="${this.maxQuantity}` : nothing}
          />
          <button
            @click=${this.increment}
            name="increase"
            class="button__increase"
          >
            ${paths.plus}
          </button>
          ${this.warningMessage
        ? html`<p
                class="warning ${this.isWarningVisible ? 'visible' : ''} ${this
            .template === 'mini-cart'
            ? 'mini-cart'
            : ''}"
              >
                ${this.warningMessage}
              </p>`
        : nothing}
        </div>
      </span>
    `
  }

  static styles = [
    styles.a11y,
    css`

      .wrapper {
        background-color: #fff;
        border-radius: 8px;
        box-sizing: border-box;
        color: #000;
        position: relative;
        display: flex;
        height: 34px;
        border-radius: 8px;
        position: relative;
      }
      :host([type='mini-cart']) .wrapper {
        border: 1px solid var(--nova-brand-neutral);
      }
      .input {
        color: var(--nova-brand-primary);
        font-size: 16px;
        font-family: var(--nova-font-serif);
        text-align: center;
        border: 0;
        width: 100%;
        max-width: 30px;
        font-size: 16px;
        flex-grow: 1;
        -webkit-appearance: none;
        appearance: none;
        -moz-appearance: textfield;
        font-family: var(--nova-font-serif);
      }
      :host([type='mini-cart']) .input {
        color: var(--nova-brand-ink);
        font-size: 14px;
      }
      button {
        width: 24px;
        flex-shrink: 0;
        border: 0;
        background-color: transparent;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #000;
        padding: 0;
      }
      button svg {
        width: 10px;
      }
      .button__increase {
        justify-content: flex-start;
      }
      .button__decrease {
        justify-content: flex-end;
      }
      :host([type='mini-cart']) button {
        color: var(--nova-brand-primary);
      }
      button:hover {
        color: #000;
      }
      .input:-webkit-autofill,
      .input:-webkit-autofill:hover,
      .input:-webkit-autofill:active {
        box-shadow: 0 0 0 10rem var(--color-background) inset !important;
        -webkit-box-shadow: 0 0 0 10rem var(--color-background) inset !important;
      }
      .input::-webkit-outer-spin-button,
      .input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      .warning {
        position: absolute;
        left: 0;
        top: calc(100% + var(--nova-space-2xs, 4px));
        height: auto;
        margin: 0;
        z-index: 2;
        width: max-content;
        background-color: var(--nova-brand-secondary, #ffffff);
        font-family: var(--nova-font-serif);
        color: var(--nova-brand-primary, #5812CD);
        border-radius: 8px;
        padding: var(--nova-space-xs, 8px) var(--nova-space-sm, 16px);
        opacity: 0;
        transition: opacity 0.5s ease-in-out;
      }
      .warning.visible {
        opacity: 1;
      }
      .warning.mini-cart {
        left: unset;
        right: 0;
        top: 0;
        transform: translateY(calc(-100% - var(--nova-space-2xs, 4px)));
      }
        
    `,
  ]
}

customElements.define('nova-quantity-input', NovaQuantityInput)